import React from 'react';
import '../assets/RateChangeConfirmModal.css';

const RateChangeConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  unendorsedLessons,
  oldRate,
  newRate,
  tutorName,
  subject,
  rateType, // 'tuteeRate' or 'tutorRate'
  hasMoreChanges // boolean indicating if there are more rate changes to process
}) => {
  if (!isOpen) return null;

  const hasUnendorsedLessons = unendorsedLessons && unendorsedLessons.length > 0;
  const isTuteeRate = rateType === 'tuteeRate';
  const rateLabel = isTuteeRate ? 'Tutee Rate' : 'Tutor Rate';

  return (
    <div className="rate-change-modal-overlay" onClick={onClose}>
      <div className="rate-change-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rate-change-modal-header">
          <h3>{rateLabel} Change Detected</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="rate-change-modal-body">
          {hasMoreChanges && (
            <div className="multiple-changes-notice">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="8" fill="#2196F3" opacity="0.1"/>
                <path d="M10 6v4M10 14h.01" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Multiple rate changes detected. Processing {rateLabel.toLowerCase()} first.</span>
            </div>
          )}
          
          <div className="rate-change-info">
            {tutorName && <p><strong>Tutor:</strong> {tutorName}</p>}
            <p><strong>Subject:</strong> {subject}</p>
            <p><strong>Rate Type:</strong> <span className="rate-type-badge">{rateLabel}</span></p>
            <p><strong>Old Rate:</strong> ${oldRate ? oldRate.toFixed(2) : 'Default'}/hr</p>
            <p><strong>New Rate:</strong> ${newRate ? newRate.toFixed(2) : 'Default'}/hr</p>
          </div>

          {hasUnendorsedLessons ? (
            <>
              <div className="unendorsed-lessons-warning">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 20h20L12 2z" fill="#FFC107"/>
                  <path d="M12 9v4M12 17h.01" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <div>
                  <h4>Unendorsed Lessons Found</h4>
                  <p>
                    There are <strong>{unendorsedLessons.length} unendorsed lesson(s)</strong> {isTuteeRate ? 'for this subject' : 'for this tutor-subject combination'}.
                  </p>
                </div>
              </div>

              <div className="lessons-list">
                <h5>Affected Lessons:</h5>
                <div className="lessons-table-container">
                  <table className="lessons-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Duration</th>
                        <th>Current {isTuteeRate ? 'Tutee' : 'Tutor'} Rate</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unendorsedLessons.map((lesson) => (
                        <tr key={lesson._id}>
                          <td>{new Date(lesson.date).toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore' })}</td>
                          <td>{lesson.duration} min</td>
                          <td>${lesson.currentRate?.toFixed(2) || 'N/A'}</td>
                          <td>
                            <span className={`status-badge ${lesson.endorsementStatus}`}>
                              {lesson.endorsementStatus || 'Pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rate-change-question">
                <p><strong>Would you like to update these unendorsed lessons with the new {rateLabel.toLowerCase()}?</strong></p>
                <p className="note">
                  Note: Endorsed lessons will never be modified. Only unendorsed lessons can be updated.
                </p>
              </div>
            </>
          ) : (
            <div className="no-unendorsed-lessons">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="20" fill="#4CAF50" opacity="0.1"/>
                <path d="M16 24l6 6 12-12" stroke="#4CAF50" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p>No unendorsed lessons found {isTuteeRate ? 'for this subject' : 'for this tutor-subject combination'}.</p>
              <p className="note">The {rateLabel.toLowerCase()} change will only affect future lessons.</p>
            </div>
          )}
        </div>

        <div className="rate-change-modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          {hasUnendorsedLessons ? (
            <>
              <button 
                className="btn-secondary" 
                onClick={() => onConfirm(false)}
              >
                Don't Update Lessons
              </button>
              <button 
                className="btn-primary" 
                onClick={() => onConfirm(true)}
              >
                Update {unendorsedLessons.length} Lesson(s)
              </button>
            </>
          ) : (
            <button 
              className="btn-primary" 
              onClick={() => onConfirm(false)}
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RateChangeConfirmModal;
